import api from '../../lib/api';

type ErrorResult = {
  ok: false;
  status: number;
  message: string;
};

export type ResolveMigrationSuccess = {
  ok: true;
  customer_id: number;
  customer_name: string;
  channel: 'telegram' | 'in_store' | 'staff_assisted' | 'other';
  expires_at: string;
  min_password_length: number;
};

export type CompleteMigrationPasswordSetupSuccess = {
  ok: true;
  customer_id: number;
  name: string;
  tel: string;
  phone_numbers: string[];
  completed_via: 'telegram' | 'in_store' | 'staff_assisted' | 'other';
  auth_token: string;
};

export type ResolvePasswordResetSuccess = {
  ok: true;
  customer_id: number;
  customer_name: string;
  channel: 'telegram' | 'in_store' | 'staff_assisted' | 'other';
  expires_at: string;
  min_password_length: number;
};

export type CompletePasswordResetSuccess = {
  ok: true;
  customer_id: number;
  name: string;
  reset_via: 'telegram' | 'in_store' | 'staff_assisted' | 'other';
};

const extractErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error || fallback;

export const resolveCustomerLoginMigration = async (
  token: string
): Promise<{ ok: true; data: ResolveMigrationSuccess } | ErrorResult> => {
  try {
    const res = await api.get<ResolveMigrationSuccess>('/api/customer-app/login/migration', {
      params: { token },
    });
    return { ok: true, data: res.data };
  } catch (error: any) {
    return {
      ok: false,
      status: error?.response?.status ?? 500,
      message: extractErrorMessage(error, 'Failed to open migration link'),
    };
  }
};

export const completeCustomerLoginMigrationPasswordSetup = async (
  token: string,
  phoneNumbers: string[],
  password: string
): Promise<{ ok: true; data: CompleteMigrationPasswordSetupSuccess } | ErrorResult> => {
  try {
    const res = await api.post<CompleteMigrationPasswordSetupSuccess>(
      '/api/customer-app/login/migration/set-password',
      { token, phone_numbers: phoneNumbers, password }
    );
    return { ok: true, data: res.data };
  } catch (error: any) {
    return {
      ok: false,
      status: error?.response?.status ?? 500,
      message: extractErrorMessage(error, 'Failed to finish login setup'),
    };
  }
};

export const resolveCustomerPasswordReset = async (
  token: string
): Promise<{ ok: true; data: ResolvePasswordResetSuccess } | ErrorResult> => {
  try {
    const res = await api.get<ResolvePasswordResetSuccess>('/api/customer-app/login/reset-password', {
      params: { token },
    });
    return { ok: true, data: res.data };
  } catch (error: any) {
    return {
      ok: false,
      status: error?.response?.status ?? 500,
      message: extractErrorMessage(error, 'Failed to open password reset link'),
    };
  }
};

export const completeCustomerPasswordReset = async (
  token: string,
  password: string
): Promise<{ ok: true; data: CompletePasswordResetSuccess } | ErrorResult> => {
  try {
    const res = await api.post<CompletePasswordResetSuccess>('/api/customer-app/login/reset-password', {
      token,
      password,
    });
    return { ok: true, data: res.data };
  } catch (error: any) {
    return {
      ok: false,
      status: error?.response?.status ?? 500,
      message: extractErrorMessage(error, 'Failed to reset password'),
    };
  }
};
